import type { ReactNode } from 'react'
import { mediaDesktop, mediaMobile, mediaTablet, mediaTabletAndMobile } from '../styles/breakPoints'
import styled, { css } from 'styled-components'

const Container = styled.div`
  padding: 80px 160px;
  background-color: var(--themeColorBetaBgLight);

  ${mediaDesktop} {
    position: relative;
    > div {
      margin: 0 auto;
      max-width: 1256px;
    }
  }
  ${mediaTablet} {
    padding: 120px 80px;
  }
  ${mediaMobile} {
    padding: 80px 24px;
  }
`

const TagLine = styled.div`
  color: var(--themeColorBetaSecondary);
  font-size: ${p => p.theme.font.size.h4};
  font-weight: ${p => p.theme.font.weight.normal};
  ${mediaMobile} {
    font-size: ${p => p.theme.font.size.title};
  }
`
const Title = styled.h2`
  ${({ theme: { font } }) => css`
    color: var(--themeColorBetaBrandHeader);
    font-family: ${font.family.header};
    font-size: 50px; // special size
    font-weight: ${font.weight.light};
    line-height: ${font.lineHeight};
    text-transform: uppercase;
    text-align: center;
    ${mediaMobile} {
      font-size: 32px;
    }
    ${mediaMobile} {
      font-size: ${p => p.theme.font.size.h1};
    }
  `}
`
const Header = styled.div`
  display: flex;
  padding: 0 0 48px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 12px;
`

const SubContainer = styled.div`
  display: flex;
  gap: 64px;
  font-style: normal;
  color: var(--themeColorBetaSecondary);
  font-size: ${p => p.theme.font.size.title};
  font-weight: ${p => p.theme.font.weight.normal};
  line-height: ${p => p.theme.font.lineHeight};
  align-items: center;
  > div {
    width: calc(50% - 32px);
  }
  img {
    width: 100%;
    border-radius: 16px;
    display: block;
  }
  ${mediaTabletAndMobile} {
    flex-direction: column;
    > div {
      width: 100%;
      max-width: 500px;
      max-height: 600px;
    }
  }
`
export const SubTitle = styled(Title)`
  font-size: ${p => p.theme.font.size.h2};
  margin-bottom: 16px;
  text-align: left;
`

const PanelGroup = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  justify-content: center;
  flex-direction: row;
  gap: 24px;
  li {
    text-align: left;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    width: 33%;

    color: var(--themeColorForegroundPrimary);
    font-size: ${p => p.theme.font.size.title};
    font-weight: ${p => p.theme.font.weight.light};
    line-height: ${p => p.theme.font.lineHeight};
    img {
      max-width: 100%;
    }
  }
  ${mediaTabletAndMobile} {
    flex-direction: column;
    align-items: center;
    gap: 48px;
    li {
      width: 100%;
      max-width: 500px;
    }
  }
`
const PanelTitle = styled.div`
  color: var(--themeColorBetaBrandHeader);
  font-size: ${p => p.theme.font.size.h4};
  font-weight: ${p => p.theme.font.weight.bold};
  line-height: ${p => p.theme.font.lineHeight};
  margin: 32px 0 4px;
  ${mediaTabletAndMobile} {
    margin: 24px 0 4px;
  }
`

type SectionProps = {
  tagLine?: string
  title?: string
  children?: ReactNode
  className?: string
}
export const Section = ({ tagLine, title, children, className }: SectionProps) => (
  <Container className={className} aria-labelledby={title}>
    {title ? (
      <Header>
        {tagLine ? <TagLine>{tagLine}</TagLine> : null}
        <Title id={title}>{title}</Title>
      </Header>
    ) : null}
    <div>{children}</div>
  </Container>
)

type SubSectionProps = {
  img?: ReactNode
  title: string
  flipSides?: boolean
  children: ReactNode
}
export const SubSection = ({ img, title, flipSides, children }: SubSectionProps) => (
  <SubContainer>
    {!flipSides && <div>{img}</div>}
    <div>
      <SubTitle>{title}</SubTitle>
      {children}
    </div>
    {flipSides ? <div>{img}</div> : null}
  </SubContainer>
)

export const SectionPanels = ({ children }) => <PanelGroup>{children}</PanelGroup>

type PanelProps = {
  img?: ReactNode
  title?: string
  className?: string
  children: ReactNode
}
export const Panel = styled(({ img, title, className, children }: PanelProps) => (
  <li className={className}>
    {img}
    <PanelTitle>{title}</PanelTitle>
    <div>{children}</div>
  </li>
))`
  padding: 0 24px;
`

export default Section
